import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Check, ShieldCheck, ChevronRight, Package, Truck, Star, Info } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import { StockBadge } from '../components/StockBadge';
import { SEO } from '../components/SEO';
import { ProductCard } from '../components/ProductCard';
import { sortProducts } from '../utils/sortProducts';
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    products
  } = useStore();
  const product = products.find(p => p.slug === slug);
  const {
    addToCart
  } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'features' | 'includes' | 'specs'>('features');
  // Related: same primary group or shared task category, sorted by popularity.
  const related = product ? sortProducts(products.filter(p => p.id !== product.id && (p.primaryGroup === product.primaryGroup || p.taskCategories?.some(t => product.taskCategories?.includes(t)))), 'popularity').slice(0, 4) : [];
  // Frequently bought together: accessories / modules sorted by price desc.
  const fbt = product ? sortProducts(products.filter(p => p.id !== product.id && (p.primaryGroup === 'Accessory' || p.primaryGroup === 'Module')), 'price-desc').slice(0, 3) : [];
  if (!product) {
    return <div className="max-w-7xl mx-auto px-4 py-24 text-center" data-id="element-1731">
        <SEO title="Product Not Found" data-id="element-1732" />
        <h1 className="text-3xl font-bold mb-4" data-id="element-1733">Product Not Found</h1>
        <Link to="/shop" className="text-yarbo-dark hover:underline" data-id="element-1734">
          Return to Shop
        </Link>
      </div>;
  }
  return <div className="bg-white" data-id="element-1735">
      <SEO title={product.name} description={product.shortDescription} data-id="element-1736" />
      {/* Breadcrumbs */}
      <div className="border-b border-gray-200" data-id="element-1737">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center text-sm text-gray-500 overflow-x-auto whitespace-nowrap hide-scrollbar" data-id="element-1738">
          <Link to="/" className="hover:text-yarbo-dark" data-id="element-1739">
            Home
          </Link>
          <ChevronRight className="w-3.5 h-3.5 mx-1.5" data-id="element-1740" />
          <Link to="/shop" className="hover:text-yarbo-dark" data-id="element-1741">
            Shop
          </Link>
          <ChevronRight className="w-3.5 h-3.5 mx-1.5" data-id="element-1742" />
          <span className="text-yarbo-dark font-medium" data-id="element-1743">{product.name}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="py-8 lg:py-12" data-id="element-1744">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1745">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16" data-id="element-1746">
            {/* Gallery */}
            <motion.div initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} data-id="element-1747">
              <div className="aspect-square bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl overflow-hidden border border-gray-200 p-8 lg:p-12 mb-4" data-id="element-1748">
                <img src={product.image} alt={product.name} className="w-full h-full object-contain" data-id="element-1749" />
              </div>
            </motion.div>

            {/* Info */}
            <motion.div initial={{
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} className="flex flex-col" data-id="element-1750">
              {product.requiresCore && <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold px-3 py-2 rounded-lg mb-4 w-fit" data-id="element-1751">
                  Module Only · Requires a compatible Yarbo Core
                </div>}

              <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2" data-id="element-1752">
                {product.category}
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3 leading-tight" data-id="element-1753">
                {product.name}
              </h1>

              <div className="flex items-center gap-3 mb-4" data-id="element-1754">
                <StockBadge status={product.status} size="md" data-id="element-1755" />
              </div>

              <div className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-6" data-id="element-1756">
                ${product.price.toLocaleString()}
              </div>

              <p className="text-base text-gray-600 mb-6 leading-relaxed" data-id="element-1757">
                {product.shortDescription}
              </p>

              <ul className="space-y-2 mb-8" data-id="element-1758">
                {product.features && product.features.length > 0 && product.features.slice(0, 4).map((f, idx) => <li key={idx} className="flex items-start text-sm text-gray-700" data-id="element-1759">
                      <Check className="w-4 h-4 text-yarbo-green mr-2 mt-0.5 flex-shrink-0" data-id="element-1760" />
                      <span data-id="element-1761">{f}</span>
                    </li>)}
              </ul>

              {/* Qty + Buy */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-1762">
                <div className="flex items-center border-2 border-gray-200 rounded-lg w-fit" data-id="element-1763">
                  <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="p-3 hover:bg-gray-50" aria-label="Decrease" data-id="element-1764">
                    <Minus className="w-4 h-4" data-id="element-1765" />
                  </button>
                  <span className="px-5 font-bold text-base" data-id="element-1766">{quantity}</span>
                  <button onClick={() => setQuantity(q => q + 1)} className="p-3 hover:bg-gray-50" aria-label="Increase" data-id="element-1767">
                    <Plus className="w-4 h-4" data-id="element-1768" />
                  </button>
                </div>
                <button onClick={() => addToCart(product, quantity)} className="flex-1 bg-yarbo-dark text-white font-bold py-4 rounded-lg hover:bg-black transition-colors flex items-center justify-center gap-2 text-base" data-id="element-1769">
                  <ButtonIcon className="w-5 h-5" data-id="element-1770" /> {ctaLabel(product.status)}
                </button>
              </div>

              {/* YarboCare note — only for eligible Complete Systems / Bundles */}
              {product.eligibleForYarboCare && <div className="mb-6 -mt-2 flex items-start gap-3 bg-yarbo-yellow/10 border border-yarbo-yellow/40 rounded-lg p-3.5" data-id="element-1771">
                  <Shield className="w-4 h-4 text-yarbo-dark mt-0.5 flex-shrink-0" data-id="element-1772" />
                  <div className="flex-1 min-w-0" data-id="element-1773">
                    <div className="text-sm font-bold text-yarbo-dark" data-id="element-1774">
                      YarboCare Protection Available
                    </div>
                    <p className="text-xs text-gray-700 mt-0.5 leading-relaxed" data-id="element-1775">
                      Add optional YarboCare extended warranty coverage at
                      checkout for eligible Yarbo systems.
                    </p>
                    <Link to="/faq#yarbocare" className="text-xs font-bold text-yarbo-dark hover:text-black underline underline-offset-2 mt-1.5 inline-block" data-id="element-1776">
                      View protection options →
                    </Link>
                  </div>
                </div>}

              {/* Trust */}
              <div className="grid grid-cols-3 gap-3 py-5 border-t border-b border-gray-100 mb-6" data-id="element-1777">
                <div className="flex items-start gap-2" data-id="element-1778">
                  <Truck className="w-4 h-4 text-yarbo-dark mt-0.5" data-id="element-1779" />
                  <div className="text-xs" data-id="element-1780">
                    <div className="font-bold text-yarbo-dark" data-id="element-1781">
                      Ships Nationwide
                    </div>
                    <div className="text-gray-500" data-id="element-1782">Across the U.S.</div>
                  </div>
                </div>
                <div className="flex items-start gap-2" data-id="element-1783">
                  <Shield className="w-4 h-4 text-yarbo-dark mt-0.5" data-id="element-1784" />
                  <div className="text-xs" data-id="element-1785">
                    <div className="font-bold text-yarbo-dark" data-id="element-1786">
                      Secure Checkout
                    </div>
                    <div className="text-gray-500" data-id="element-1787">Encrypted</div>
                  </div>
                </div>
                <div className="flex items-start gap-2" data-id="element-1788">
                  <Package className="w-4 h-4 text-yarbo-dark mt-0.5" data-id="element-1789" />
                  <div className="text-xs" data-id="element-1790">
                    <div className="font-bold text-yarbo-dark" data-id="element-1791">
                      {product.status === 'in-stock' ? 'Ready to Ship' : product.status === 'pre-order' ? 'Pre-Order' : 'Coming Soon'}
                    </div>
                    <div className="text-gray-500" data-id="element-1792">
                      {product.status === 'in-stock' ? 'In stock' : 'Reserve now'}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-gray-50 py-14 border-t border-gray-200" data-id="element-1793">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1794">
          <div className="flex space-x-1 bg-gray-200 p-1 rounded-xl mb-6 overflow-x-auto hide-scrollbar" data-id="element-1795">
            {[{
            key: 'features',
            label: 'Product Details'
          }, {
            key: 'specs',
            label: 'Specifications'
          }].map(t => <button key={t.key} onClick={() => setActiveTab(t.key as any)} className={`flex-1 py-3 px-4 text-sm font-bold rounded-lg whitespace-nowrap transition-colors ${activeTab === t.key ? 'bg-white text-yarbo-dark shadow-sm' : 'text-gray-600'}`} data-id="element-1796">
                {t.label}
              </button>)}
          </div>

          <div className="bg-white p-8 rounded-2xl border border-gray-200" data-id="element-1797">
            {activeTab === 'features' && <div className="prose max-w-none text-gray-700" data-id="element-1798">
                {product.requiresCore && <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium p-4 rounded-lg mb-5 not-prose" data-id="element-1799">
                    This is a module-only product. The Yarbo Core is required
                    for operation and is sold separately or as part of a
                    complete system.
                  </div>}
                {product.descriptionHtml ? <div dangerouslySetInnerHTML={{
              __html: product.descriptionHtml
            }} data-id="element-1800" /> : <p data-id="element-1801">{product.descriptionText || product.shortDescription}</p>}
              </div>}

            {activeTab === 'specs' && <div className="space-y-4" data-id="element-1802">
                <div className="grid grid-cols-3 py-3 border-b border-gray-100" data-id="element-1803">
                  <div className="text-gray-500 font-medium" data-id="element-1804">Category</div>
                  <div className="col-span-2 text-gray-900 font-medium" data-id="element-1805">
                    {product.category}
                  </div>
                </div>
                <div className="grid grid-cols-3 py-3 border-b border-gray-100" data-id="element-1806">
                  <div className="text-gray-500 font-medium" data-id="element-1807">Season</div>
                  <div className="col-span-2 text-gray-900 font-medium" data-id="element-1808">
                    {product.season.join(', ')}
                  </div>
                </div>
                <div className="grid grid-cols-3 py-3 border-b border-gray-100" data-id="element-1809">
                  <div className="text-gray-500 font-medium" data-id="element-1810">SKU</div>
                  <div className="col-span-2 text-gray-900 font-medium" data-id="element-1811">
                    {product.sku}
                  </div>
                </div>
                {product.weightLbs && <div className="grid grid-cols-3 py-3 border-b border-gray-100" data-id="element-1812">
                    <div className="text-gray-500 font-medium" data-id="element-1813">Weight</div>
                    <div className="col-span-2 text-gray-900 font-medium" data-id="element-1814">
                      {product.weightLbs} lbs
                      {product.weightNote && <span className="block text-xs text-gray-400 mt-1" data-id="element-1815">
                          {product.weightNote}
                        </span>}
                    </div>
                  </div>}
                {product.brochureUrl && <div className="grid grid-cols-3 py-3 border-b border-gray-100" data-id="element-1816">
                    <div className="text-gray-500 font-medium" data-id="element-1817">Documents</div>
                    <div className="col-span-2 text-gray-900 font-medium" data-id="element-1818">
                      <a href={product.brochureUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-bold" data-id="element-1819">
                        Product Brochure
                      </a>
                    </div>
                  </div>}
              </div>}
          </div>
        </div>
      </section>

      {/* Frequently bought together */}
      {fbt.length > 0 && <section className="py-14 border-t border-gray-200" data-id="element-1820">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1821">
            <h2 className="text-2xl font-extrabold text-yarbo-dark mb-6" data-id="element-1822">
              Frequently Bought Together
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-id="element-1823">
              {fbt.map(p => <ProductCard key={p.id} product={p} compact data-id="element-1824" />)}
            </div>
          </div>
        </section>}

      {/* Related */}
      {related.length > 0 && <section className="py-14 bg-gray-50 border-t border-gray-200" data-id="element-1825">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1826">
            <h2 className="text-2xl font-extrabold text-yarbo-dark mb-6" data-id="element-1827">
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6" data-id="element-1828">
              {related.map(p => <ProductCard key={p.id} product={p} compact data-id="element-1829" />)}
            </div>
          </div>
        </section>}

      {/* Sticky mobile Add to Cart */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-40 shadow-[0_-4px_12px_-2px_rgba(0,0,0,0.08)]" data-id="element-1830">
        <div className="flex items-center gap-3" data-id="element-1831">
          <div className="flex-1 min-w-0" data-id="element-1832">
            <div className="text-sm font-bold text-yarbo-dark truncate" data-id="element-1833">
              {product.name}
            </div>
            <div className="text-lg font-extrabold text-yarbo-dark" data-id="element-1834">
              ${product.price.toLocaleString()}
            </div>
          </div>
          <button onClick={() => addToCart(product, quantity)} className="bg-yarbo-dark text-white font-bold px-5 py-3 rounded-lg flex items-center gap-2 whitespace-nowrap" data-id="element-1835">
            <ButtonIcon className="w-4 h-4" data-id="element-1836" /> {ctaLabel(product.status)}
          </button>
        </div>
      </div>
    </div>;
}